import React from 'react';
import type { DropResult } from '@hello-pangea/dnd';
import { useSelector } from 'store';
import { persistSidebarLayoutToServer } from 'utils/chat-sidebar-server-sync';
import {
  DEFAULT_CHAT_SIDEBAR_VIEW_IDS,
  type DefaultChatSidebarViewId,
} from 'utils/default-chat-sidebar-views';

const STORAGE_KEY = 'funner.chatSidebar.systemViewOrder.v1';

export type ChatSidebarLayoutPreset = 'chronological' | 'operational' | 'custom';

type Persisted = {
  preset: ChatSidebarLayoutPreset;
  /** כאשר preset === custom */
  order: DefaultChatSidebarViewId[];
};

/** סדר כרונולוגי 1–6 (כמו ב־DEFAULT_CHAT_SIDEBAR_VIEW_IDS) */
export const CHRONOLOGICAL_VIEW_ORDER = [...DEFAULT_CHAT_SIDEBAR_VIEW_IDS];

/**
 * סדר תפעולי לפי דחיפות: מענה שוטף → מענה ראשוני → תור כללי → המתנה → תזכורת → ארכיון
 */
export const OPERATIONAL_VIEW_ORDER: DefaultChatSidebarViewId[] = [
  'awaiting-me',
  'newly-assigned',
  'unassigned-chats',
  'waiting-customer',
  'my-chats-snoozed',
  'chats-completed',
];

function isValidOrder(arr: unknown): arr is DefaultChatSidebarViewId[] {
  if (!Array.isArray(arr) || arr.length !== DEFAULT_CHAT_SIDEBAR_VIEW_IDS.length) return false;
  const set = new Set(arr);
  return DEFAULT_CHAT_SIDEBAR_VIEW_IDS.every((id) => set.has(id));
}

function loadPersisted(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { preset: 'chronological', order: [...CHRONOLOGICAL_VIEW_ORDER] };
    }
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    const preset =
      parsed.preset === 'operational' || parsed.preset === 'custom' || parsed.preset === 'chronological'
        ? parsed.preset
        : 'chronological';
    if (preset === 'chronological') {
      return { preset, order: [...CHRONOLOGICAL_VIEW_ORDER] };
    }
    if (preset === 'operational') {
      return { preset, order: [...OPERATIONAL_VIEW_ORDER] };
    }
    if (isValidOrder(parsed.order)) {
      return { preset: 'custom', order: parsed.order };
    }
  } catch {
    /* ignore */
  }
  return { preset: 'chronological', order: [...CHRONOLOGICAL_VIEW_ORDER] };
}

function savePersisted(p: Persisted) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

/** ולידציה של ערך סדר שהגיע מהשרת (chatSidebarLayout.systemViewOrder) — מבנה זהה ל-Persisted */
function normalizeServerOrder(raw: { preset: string; order: string[] }): Persisted {
  if (raw.preset === 'operational') return { preset: 'operational', order: [...OPERATIONAL_VIEW_ORDER] };
  if (raw.preset === 'custom' && isValidOrder(raw.order)) return { preset: 'custom', order: raw.order };
  return { preset: 'chronological', order: [...CHRONOLOGICAL_VIEW_ORDER] };
}

export type ChatSidebarSystemOrderValue = {
  preset: ChatSidebarLayoutPreset;
  orderedIds: DefaultChatSidebarViewId[];
  setChronological: () => void;
  setOperational: () => void;
  onDragEnd: (result: DropResult) => void;
};

const ChatSidebarSystemOrderContext = React.createContext<ChatSidebarSystemOrderValue | null>(null);

function useChatSidebarSystemOrderState(): ChatSidebarSystemOrderValue {
  const [state, setState] = React.useState<Persisted>(() =>
    typeof window === 'undefined' ? { preset: 'chronological', order: [...CHRONOLOGICAL_VIEW_ORDER] } : loadPersisted(),
  );

  const stateRef = React.useRef(state);
  stateRef.current = state;

  //* אימוץ חד-פעמי מהשרת: השרת הוא מקור האמת; localStorage נשאר כ-fallback מהיר לעלייה.
  //* שרת בלי ערך + קונפיג שנטען = מיגרציה חד-פעמית של הערך הלוקאלי לשרת.
  const serverOrder = useSelector((s) => s.userConfig?.chatSidebarLayout?.systemViewOrder);
  const configLoaded = useSelector((s) => s.userConfig?.userConfigLoadedFromServer);
  const adoptedRef = React.useRef(false);
  const mutatedRef = React.useRef(false);

  React.useEffect(() => {
    if (adoptedRef.current || !configLoaded || mutatedRef.current) return;
    adoptedRef.current = true;
    if (serverOrder && typeof serverOrder.preset === 'string') {
      const next = normalizeServerOrder(serverOrder);
      setState(next);
      savePersisted(next);
    } else {
      persistSidebarLayoutToServer({ systemViewOrder: stateRef.current });
    }
  }, [configLoaded, serverOrder]);

  const setChronological = React.useCallback(() => {
    const next: Persisted = { preset: 'chronological', order: [...CHRONOLOGICAL_VIEW_ORDER] };
    mutatedRef.current = true;
    setState(next);
    savePersisted(next);
    persistSidebarLayoutToServer({ systemViewOrder: next });
  }, []);

  const setOperational = React.useCallback(() => {
    const next: Persisted = { preset: 'operational', order: [...OPERATIONAL_VIEW_ORDER] };
    mutatedRef.current = true;
    setState(next);
    savePersisted(next);
    persistSidebarLayoutToServer({ systemViewOrder: next });
  }, []);

  const onDragEnd = React.useCallback((result: DropResult) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;
    setState((prev) => {
      const nextOrder = [...prev.order];
      const [removed] = nextOrder.splice(source.index, 1);
      nextOrder.splice(destination.index, 0, removed);
      const next: Persisted = { preset: 'custom', order: nextOrder };
      mutatedRef.current = true;
      savePersisted(next);
      persistSidebarLayoutToServer({ systemViewOrder: next });
      return next;
    });
  }, []);

  return {
    preset: state.preset,
    orderedIds: state.order,
    setChronological,
    setOperational,
    onDragEnd,
  };
}

export function ChatSidebarSystemOrderProvider({ children }: { children: React.ReactNode }) {
  const value = useChatSidebarSystemOrderState();
  return React.createElement(ChatSidebarSystemOrderContext.Provider, { value }, children);
}

export function useChatSidebarSystemOrder(): ChatSidebarSystemOrderValue {
  const ctx = React.useContext(ChatSidebarSystemOrderContext);
  if (!ctx) {
    throw new Error('useChatSidebarSystemOrder must be used within ChatSidebarSystemOrderProvider');
  }
  return ctx;
}
